/**
 * The agent side panel's Jobs tab (T15).
 *
 * Lists the customer's jobs (from the account context, same place
 * AgentTocTab/AgentVariablesTab already read their job list — no new API
 * call) with a checkbox each, ticked for the session's current selection.
 * Ticking writes the COMPLETE new set through the user-facing session-jobs
 * route (T4); an agent-driven `set_jobs` call moves the boxes on its own via
 * the `sessionJobs` prop, which is fed live from the event stream reducer
 * (T14) and always wins once non-null.
 *
 * Precedence (design doc, "Reducer"): `sessionJobs ?? fallbackJobs ?? []` —
 * `fallbackJobs` is the session row's `jobs` as loaded, used only until the
 * first `session_jobs_changed` event arrives.
 */
import React, { useEffect, useState } from 'react'
import { Box, Checkbox, Typography, Alert } from '@mui/material'

import useAccount from '../../hooks/useAccount'
import { agentSessionJobsApi } from '../../services/agentSessionJobsApi'

export interface AgentJobsTabProps {
  customer: string
  sessionId: string
  /** Live selection from the event stream; null until the first event fires. */
  sessionJobs: string[] | null
  /** The session row's jobs as loaded, used only until an event arrives. */
  fallbackJobs: string[]
  /**
   * Called with the new set once a user tick has been persisted. The host
   * feeds it into the SAME state an agent-driven `session_jobs_changed` writes
   * to, so the Tables and Variables job pickers narrow immediately as well.
   * Without it a user's tick stays local to this tab and the other two keep
   * offering the old selection until a reload.
   */
  onJobsChanged?: (jobs: string[]) => void
}

export default function AgentJobsTab({ customer, sessionId, sessionJobs, fallbackJobs, onJobsChanged }: AgentJobsTabProps) {
  const account = useAccount()
  const sessionCust = account.user?.session_info?.sessionCusts?.find(c => c.name === customer)
  const availableJobs = (sessionCust?.sessionJobs || [])
    .filter((j): j is { name: string; displayName?: string | null } => Boolean(j.name))

  const effectiveJobs = sessionJobs ?? fallbackJobs ?? []

  // Optimistic overlay: the boxes move the instant the user clicks, before the
  // write round-trips. Cleared whenever the resolved prop selection changes —
  // either a real `session_jobs_changed` event lands (T14), matching what was
  // just optimistically shown, or the parent's fallback moves — so the prop
  // is always the source of truth once it changes.
  const [optimisticJobs, setOptimisticJobs] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // One write at a time. Each click used to read `ticked` at click time, so a
  // second tick landing before the first PUT returned computed its new set from
  // a selection the server had not seen — and whichever request the server
  // applied last won, silently dropping the other. Locking the boxes for the
  // round trip makes that unrepresentable rather than merely unlikely.
  const [pending, setPending] = useState(false)
  useEffect(() => {
    setOptimisticJobs(null)
  }, [JSON.stringify(effectiveJobs)])

  const ticked = optimisticJobs ?? effectiveJobs

  const handleToggle = async (job: string, checked: boolean) => {
    if (pending) return
    const previous = ticked
    const next = checked ? [...previous, job] : previous.filter(j => j !== job)
    setOptimisticJobs(next)
    setError(null)
    // Before a session exists (the start screen, Adam 10 Sep) there is nothing
    // to POST to: the tick IS the selection, and the session is created with
    // it. The parent owns it through onJobsChanged.
    if (!sessionId) {
      onJobsChanged?.(next)
      return
    }
    setPending(true)
    try {
      const resp = await agentSessionJobsApi.setJobs(customer, sessionId, next)
      // The server is the authority on what the row now holds: it normalises
      // (dedupes, orders) and can refuse part of a request, so showing the
      // local guess would leave the tab claiming a selection the session does
      // not have. Fall back to `next` only if an older server answers without
      // a jobs array.
      const applied = Array.isArray(resp?.jobs) ? resp.jobs : next
      setOptimisticJobs(applied)
      // Only after the write succeeds: publish to shared state so every other
      // tab narrows too. On failure we fall through to the revert below and
      // nothing outside this tab ever saw the change.
      onJobsChanged?.(applied)
    } catch (err) {
      setOptimisticJobs(previous)
      setError(err instanceof Error ? err.message : 'Failed to update jobs')
    } finally {
      setPending(false)
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1, overflow: 'auto' }}>
      {ticked.length === 0 && (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 1 }}>
          This session has no job selected — choose at least one job below.
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ fontSize: 12 }}>{error}</Alert>}
      {/* Rows on the same spacing as the Tables and Variables trees (Adam,
          11 Sep): a 20px box, 14px label, 22px minimum row, hover wash. The
          whole row is the control, as a tree row is. */}
      {availableJobs.map(job => {
        const label = job.displayName || job.name
        const checked = ticked.includes(job.name)
        return (
          <Box
            key={job.name}
            onClick={() => handleToggle(job.name, !checked)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minHeight: 22,
              py: '6px',
              px: 1,
              cursor: pending ? 'default' : 'pointer',
              opacity: pending ? 0.6 : 1,
              borderRadius: '6px',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Checkbox
              checked={checked}
              disabled={pending}
              size="medium"
              onChange={(e) => handleToggle(job.name, e.target.checked)}
              // The row already toggles on click; the box must not toggle twice.
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0, flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 20 } }}
              inputProps={{ 'aria-label': label }}
            />
            <Typography sx={{ fontSize: 14, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}
